import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { toaster } from '@optiaxiom/react';

import {
  chatBodyReadyAtom,
  opalOverlayInitialMessagingAtom,
  opalOverlayOpenAtom,
} from '@frontend/atoms';
import { EVENT_NAME } from '@frontend/constants';

import { getPromptQueue } from '../remote/promptQueue';

import { useMessagingMode } from './useMessagingMode';

// If the chat body is slow to signal ready, surface a soft "still loading" notice
// after this long — but keep waiting. A federated cold-load can be slow-but-successful,
// and the READY listener is already bounded to this open session (a new call, the
// overlay closing, or unmount aborts it), so a late mount still delivers the prompt.
const READY_TIMEOUT_MS = 10_000;

/** Start a fresh thread and enqueue the prompt into the (now-mounted) chat body. */
function sendToFreshThread(text: string) {
  // Empty threadId ⇒ the chat starts a new thread (see useSetThreadEvent).
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME.OPAL_CHAT_SET_THREAD, { detail: { threadId: undefined } })
  );
  void getPromptQueue()
    .then(promptQueue =>
      promptQueue().enqueuePrompt({
        type: 'INITIAL_PROMPT',
        payload: [{ content: text, role: 'user' }],
      })
    )
    .catch(error => {
      // Covers both a failed enqueue and a failed remote load (the fallback queue
      // rejects). Surface it so the prompt isn't silently dropped after the overlay
      // already opened a blank thread.
      console.error('Ask Opal: failed to enqueue prompt', error);
      toaster.create('Your prompt could not be sent. Please try again.', { intent: 'danger' });
    });
}

/**
 * Opens the embedded Opal chat overlay and auto-sends `text` as a user turn in a
 * brand-new thread (never the overlay's last-selected one).
 *
 * Entry states (keyed on the positively-confirmed messaging tab):
 * - Closed → open on the ai-chat tab, wait for `OPAL_CHAT_READY`, then reset + send.
 * - Open, confirmed ai-chat → send now IF the chat body is mounted; if not (e.g. the
 *   overlay was opened via the header button and the MFE is still loading), arm READY
 *   first so the transient thread-reset event isn't dropped before its listener exists.
 * - Open on the messages tab, or tab not yet known → `initialMessaging` is only read
 *   on mount, so close and reopen on ai-chat (the close-effect drives the reopen), then
 *   send on READY. Requiring a positive 'ai-chat' (rather than "not messages") means an
 *   unknown tab reopens safely instead of sending blindly into a possibly-messages body.
 *
 * The pending READY listener is owned by an `AbortController` so it can never leak:
 * a new call, the overlay closing (other than our own reopen), or unmount all cancel
 * it. Otherwise a stale listener would fire on the next chat mount for any reason
 * (e.g. opening chat from the header), injecting an old prompt and resetting the
 * user's restored thread.
 */
export function useAskOpal() {
  const overlayOpen = useAtomValue(opalOverlayOpenAtom);
  const setOverlayOpen = useSetAtom(opalOverlayOpenAtom);
  const setOverlayMessaging = useSetAtom(opalOverlayInitialMessagingAtom);
  const { mode: messagingMode } = useMessagingMode();

  const pendingRef = useRef<AbortController | null>(null);
  // Prompt awaiting an intentional close→reopen (messages tab → ai-chat tab).
  const reopenTextRef = useRef<string | null>(null);
  // Whether the federated chat body is mounted (its SET_THREAD listener exists). Owned by
  // OpalChatOverlay, which flips it false on every unmount path — not just close — so this
  // never goes stale-true and we never send into a dead body.
  const bodyReady = useAtomValue(chatBodyReadyAtom);

  const armAndOpen = useCallback(
    (text: string) => {
      pendingRef.current?.abort();
      const controller = new AbortController();
      pendingRef.current = controller;
      const timer = setTimeout(() => {
        // Keep the READY listener armed — a slow mount still delivers the prompt; only
        // nudge the user. Cleanup on close/supersede/unmount still aborts the listener.
        toaster.create('Opal chat is taking a moment to load…', { intent: 'warning' });
      }, READY_TIMEOUT_MS);
      controller.signal.addEventListener('abort', () => clearTimeout(timer));
      window.addEventListener(
        EVENT_NAME.OPAL_CHAT_READY,
        () => {
          controller.abort(); // consume: clear the timer, drop the listener
          sendToFreshThread(text);
        },
        { once: true, signal: controller.signal }
      );
      // Force the Opal-chat tab (its body hosts the drainer/sender) — read on mount.
      setOverlayMessaging({ mode: 'ai-chat' });
      setOverlayOpen(true);
    },
    [setOverlayMessaging, setOverlayOpen]
  );

  // On close: reopen on ai-chat if we closed to switch tabs, else cancel the pending
  // request so a leaked listener can't refire on a later, unrelated chat mount.
  useEffect(() => {
    if (overlayOpen) return;
    const reopenText = reopenTextRef.current;
    if (reopenText !== null) {
      reopenTextRef.current = null;
      // Defer past this synchronous effect flush so armAndOpen's
      // setOverlayMessaging({ mode: 'ai-chat' }) always lands after OpalChatOverlay's
      // close clear-effect, independent of sibling render order in BaseLayout.
      queueMicrotask(() => armAndOpen(reopenText));
    } else {
      pendingRef.current?.abort();
    }
  }, [overlayOpen, armAndOpen]);
  useEffect(() => () => pendingRef.current?.abort(), []);

  return useCallback(
    (rawText: string) => {
      const text = rawText.trim();
      if (!text) return;
      pendingRef.current?.abort();

      if (overlayOpen && messagingMode === 'ai-chat') {
        // Confirmed on the ai-chat surface. Send now only if the body is mounted (its
        // SET_THREAD listener exists); otherwise the reset event would be lost, so
        // arm the READY handshake first.
        if (bodyReady) sendToFreshThread(text);
        else armAndOpen(text);
        return;
      }

      if (overlayOpen) {
        // On the messages tab, or the tab isn't known yet: close now and let the
        // close-effect reopen on ai-chat (forced via initialMessaging), then send on
        // READY. Never send into a body we haven't confirmed is on ai-chat.
        reopenTextRef.current = text;
        setOverlayOpen(false);
        return;
      }

      armAndOpen(text);
    },
    [overlayOpen, messagingMode, bodyReady, armAndOpen, setOverlayOpen]
  );
}
